import type { AccessRuleInputData } from '@sunstone/api-client/hooks';

export type ResourceKind = 'deployment' | 'workspace';
export type AccessRuleEffect = 'allow' | 'deny';
export type RestrictionKind = 'id_based' | 'rule_based';

/**
 * One not-yet-saved deployment-kind or workspace-kind row, edited in the
 * invite wizard or the member-detail edit page. Deployment rules and
 * workspace rules are independent lists (SUN-408 round 3) — a row never
 * references another row; the evaluator matches every row by resource_kind
 * + resource ids/condition alone. `sourceId` is set only when a draft was
 * seeded from an existing `AccessRule` (edit page) — it's how the edit
 * page's save step tells "unchanged" apart from "edited" apart from "brand
 * new" (there's no update-in-place endpoint, so an edited draft becomes a
 * revoke-then-create pair; see UserAccessWizard.tsx).
 */
export interface RuleDraft {
  draftKey: string;
  sourceId?: string;
  effect: AccessRuleEffect;
  restrictionKind: RestrictionKind;
  resourceIds: string[];
  ruleAttribute: string;
  ruleValue: string;
  resourceKind: ResourceKind;
}

export function emptyRuleDraft(effect: AccessRuleEffect, resourceKind: ResourceKind): RuleDraft {
  return {
    draftKey: crypto.randomUUID(),
    effect,
    restrictionKind: 'id_based',
    resourceIds: [],
    ruleAttribute: '',
    ruleValue: '',
    resourceKind,
  };
}

export function ruleDraftHasContent(draft: RuleDraft): boolean {
  return draft.restrictionKind === 'id_based'
    ? draft.resourceIds.length > 0
    : !!draft.ruleAttribute && !!draft.ruleValue;
}

export function ruleDraftConditionJson(draft: RuleDraft): string {
  return draft.restrictionKind === 'rule_based' && draft.ruleAttribute && draft.ruleValue
    ? JSON.stringify([{ attribute: draft.ruleAttribute, op: 'eq', value: draft.ruleValue }])
    : '';
}

function ruleDraftToAccessRuleInput(draft: RuleDraft): AccessRuleInputData {
  const shape =
    draft.restrictionKind === 'id_based'
      ? { restrictionKind: 'id_based' as const, resourceIds: draft.resourceIds }
      : {
          restrictionKind: 'rule_based' as const,
          ruleConditionJson: ruleDraftConditionJson(draft),
        };
  return { ...shape, effect: draft.effect, resourceKind: draft.resourceKind };
}

/**
 * Flattens one kind's draft list into the array shape
 * `CreateInvitationRequest.access_rules` expects, dropping any row with no
 * content. Call once per list — deployment and workspace rows never need to
 * reference each other, so there's no shared index/ordering to compute.
 */
export function rulesToAccessRuleInputs(drafts: RuleDraft[]): AccessRuleInputData[] {
  return drafts.filter(ruleDraftHasContent).map(ruleDraftToAccessRuleInput);
}

export function ruleDraftsEqual(a: RuleDraft, b: RuleDraft): boolean {
  return (
    a.effect === b.effect &&
    a.restrictionKind === b.restrictionKind &&
    a.ruleAttribute === b.ruleAttribute &&
    a.ruleValue === b.ruleValue &&
    a.resourceIds.length === b.resourceIds.length &&
    a.resourceIds.every((id) => b.resourceIds.includes(id))
  );
}

/** Shape common to a persisted AccessRule row, for splitting/converting. */
export interface PersistedAccessRule {
  id: string;
  effect: string;
  restrictionKind: string;
  resourceIds: string[];
  ruleConditionJson: string;
  resourceKind: string;
}

function parseConditionField(json: string, field: 'attribute' | 'value'): string {
  try {
    const entries = JSON.parse(json) as Record<string, string>[];
    return entries[0]?.[field] ?? '';
  } catch {
    return '';
  }
}

export function accessRuleToDraft(rule: PersistedAccessRule): RuleDraft {
  return {
    draftKey: rule.id,
    sourceId: rule.id,
    effect: rule.effect === 'allow' ? 'allow' : 'deny',
    restrictionKind: rule.restrictionKind === 'rule_based' ? 'rule_based' : 'id_based',
    resourceIds: rule.resourceIds,
    ruleAttribute: parseConditionField(rule.ruleConditionJson, 'attribute'),
    ruleValue: parseConditionField(rule.ruleConditionJson, 'value'),
    resourceKind: rule.resourceKind === 'deployment' ? 'deployment' : 'workspace',
  };
}

/**
 * Splits a user's persisted access_rules rows by resource kind into two
 * independent draft lists (SUN-408 round 3) — the backend evaluator never
 * linked a workspace-kind row to a deployment-kind row, so neither does the
 * UI; this is a plain filter, not a pairing. Shared by UserDetailPage's
 * Access rules tab and UserAccessWizard (seeding the editable draft).
 */
export function splitRulesByKind(rules: PersistedAccessRule[]): {
  deployment: RuleDraft[];
  workspace: RuleDraft[];
} {
  return {
    deployment: rules.filter((r) => r.resourceKind === 'deployment').map(accessRuleToDraft),
    workspace: rules.filter((r) => r.resourceKind !== 'deployment').map(accessRuleToDraft),
  };
}
